// import jsonpatch from 'fast-json-patch'
import { api } from '~/plugins/api'
import moment from 'moment'

let instantiatedState
const updateInstance = current => {
  instantiatedState =
    !current ||
    current.coachingStage === 'done' ||
    current.coachingStage === 'discarded'
      ? null
      : api.models.Coaching.createSync(current, true)
}

export const state = () => ({
  current: null,
  offlineSync: {},
  lastCoaching: null,
  creating: false,
  isLoading: false,
})

export const getters = {
  current(state) {
    const { current } = state
    if (!current) {
      if (instantiatedState) {
        updateInstance(null)
      }
      return null
    }
    if (!instantiatedState) {
      updateInstance(current)
    }
    return instantiatedState
  },
  currentStage: (state, getters) => {
    const { current } = state
    if (!current) return null
    return current.stages.find(({ completed }) => !completed)
  },
  availableForms: (state, getters) => {
    const { current } = state
    if (!current) return []
    const { forms, coachingStage } = current
    return forms.filter(form => form.stage === coachingStage)
  },
  currentQuestions: (state, getters) => {
    const stage = getters.currentStage
    if (!stage) return null
    const { form } = stage
    if (!form) return null
    const { questions } = form
    return {
      count: questions.length,
      answered: questions.filter(item => item?.answer).length,
      list: questions,
    }
  },
  pdvsWithStatus: (state, getters) => {
    const { current } = state
    if (!current) return { in: [], out: [] }
    const { routes, stages } = current
    const pdvStatuses = [...stages]
      .reverse()
      .filter(stage => stage.pdv && stage.pdv.status !== 'discard')
      .reduce((acc, cur) => {
        const { pdv } = cur
        return acc[pdv.clientCode]
          ? acc
          : {
              ...acc,
              [pdv.clientCode]: {
                status: pdv.status,
                invalidated: pdv.invalidated,
              },
            }
      }, {})
    const statusMap = pdv => {
      const pdvStatus = pdvStatuses[pdv.code]
      return !pdvStatus
        ? { ...pdv, status: 'waiting', invalidated: false }
        : { ...pdv, ...pdvStatus }
    }
    return {
      in: routes.in.map(statusMap),
      out: routes.out.map(statusMap),
    }
  },
  hasCoachingToday: (state, getters, rootState) => {
    const teleSales = !!rootState.auth.user?.teleSales
    if (teleSales) return false
    const { lastCoaching } = state
    if (!lastCoaching) return false
    const today = moment()
    const year = today.year()
    const week = today.isoWeek()
    const day = today.weekday()
    return (
      year === lastCoaching.year &&
      week === lastCoaching.week &&
      day === lastCoaching.day
    )
  },
  clientsCoachingFeedbackMap(state) {
    return state.clientsLastCoachingFeedbackMap
  },
  employeesCoachingFeedbackMap(state) {
    return state.employeesLastCoachingFeedbackMap
  },
}

export const mutations = {
  UPDATE_ROOT: (state, payload) =>
    Object.assign(state, JSON.parse(JSON.stringify(payload))),
  UPDATE_SYNC: (state, payload) =>
    Object.assign(state.offlineSync, {
      [payload.id]: JSON.parse(JSON.stringify(payload.data)),
    }),
  UPDATE_CURRENT: (state, coaching) => {
    const finishedStates = ['done', 'discarded']
    const current =
      coaching &&
      !coaching.expired &&
      !finishedStates.includes(coaching.coachingStage)
        ? coaching
        : null
    if (!!current !== !!instantiatedState) {
      updateInstance(current)
    }
    if (coaching) {
      Object.assign(state.offlineSync, {
        [coaching._id]: { state: coaching, sync: false },
      })
    }
    Object.assign(state, { current })
  },
  REMOVE_SYNC: (state, id) => {
    delete state.offlineSync[id]
  },
  UPDATE_LAST_COACHING_FEEDBACK: (state, payload) => {
    state.clientsLastCoachingFeedbackMap =
      payload.clientsLastCoachingFeedbackMap ?? new Map()
    state.employeesLastCoachingFeedbackMap =
      payload.employeesLastCoachingFeedbackMap ?? new Map()
  },
}

export const actions = {
  async applyState({ commit }, coaching) {
    const { changeHistory } = coaching
    const lastChange = changeHistory.entries[changeHistory.entries.length - 1]
    const now = new Date()
    changeHistory.inconsistent = now < lastChange
    changeHistory.entries.push(now)
    coaching.updatedAt = now
    // await coaching.validate()
    const current = JSON.parse(JSON.stringify(coaching))
    commit('UPDATE_CURRENT', current)
  },
  async serverSync({ state, dispatch, commit }) {
    if (navigator.onLine === false) return false
    try {
      const queue = Object.values(state.offlineSync).filter(item => !item.sync)
      if (!queue.length) return null
      commit('offline/UPDATING', 'coaching', { root: true })
      for (let item of queue) {
        const { sync, state: coaching } = item
        if (sync) continue
        const { _id } = coaching
        await dispatch('trySyncState', coaching)
        if (
          coaching.coachingStage === 'done' ||
          coaching.coachingStage === 'discarded'
        ) {
          commit('REMOVE_SYNC', _id)
          continue
        }
        const newSyncData = {
          id: _id,
          data: {
            ...item,
            sync: true,
          },
        }
        commit('UPDATE_SYNC', newSyncData)
      }
      commit('offline/UPDATE', 'coaching', { root: true })
    } catch (e) {
      commit('offline/FAILED', 'coaching', { root: true })
      $nuxt.$rollbar.error(e)
      return false
    }
  },
  async trySyncState(_, coaching) {
    if (this.$api.services.server.$tokenExpired) {
      // A sincronização ignora token expirado, então e necessário tentar atualizar antes
      try {
        await this.$api.services.server.$forceRefreshToken()
      } catch (e) {
        console.error('Falha ao atualizar token para sincronização', e)
      }
    }
    return this.$axios.post('/v3/app/sync/coaching/state', coaching, {
      ignoreExpiredToken: true,
    })
  },
  async fetchLastCoaching({ commit, state }) {
    try {
      if (!navigator.onLine) return
      commit('UPDATE_ROOT', { isLoading: true })
      const [dateResponse, coachingResponse] = await Promise.all([
        this.$axios.get('/v3/app/sync/coaching/lastDate'),
        this.$axios.get('/v3/app/sync/coaching/last'),
      ])

      const lastCoaching = dateResponse.data
      commit('UPDATE_ROOT', { lastCoaching })

      const coaching = coachingResponse.data
      if (!coaching || state.current) return
      const today = moment()
      if (
        coaching.weekDate.year === today.year() &&
        coaching.weekDate.week === today.isoWeek() &&
        coaching.weekDate.day === today.weekday()
      ) {
        commit('UPDATE_CURRENT', coaching)
        this.app.$ensureRoute()
        this.$axios.post('/v3/app/coaching/automatic-recover', {
          coachingId: coaching._id,
        })
      }
    } catch (e) {
      $nuxt.$rollbar.error(e)
    } finally {
      commit('UPDATE_ROOT', { isLoading: false })
    }
  },
  async start({ commit, dispatch, rootState, rootGetters, state }, payload) {
    if (state.current || state.creating) return
    try {
      commit('UPDATE_ROOT', { creating: true, isLoading: true })
      const { coachee, weekday, salesman } = payload
      const { services } = this.$api
      const { user: coach } = rootState.auth
      const parameters = rootState.parameters.available
      const findRoutes = rootGetters['routePlan/clientsByEmployee']
      const findForms = rootGetters['forms/filterByJob']
      const titles = {
        salesman: 'Vendedor',
        coordinator: 'Coordenador',
      }
      const coacheeCode = coachee ? coachee.code : null
      const coacheeJobTitle = coachee ? coachee.jobTitle : 'salesman'
      const salesmanCode = salesman ? salesman.code : null
      const routes = findRoutes({
        salesmanCode,
        weekday,
        coachee: {
          code: coacheeCode,
          jobTitle: coacheeJobTitle,
        },
      })
      const forms = findForms(titles[coacheeJobTitle])
      const today = moment()
      const weekDate = {
        year: today.year(),
        week: today.isoWeek(),
        day: today.weekday(),
      }
      const coaching = await services.coaching.start({
        activeAllowanceClients:
          rootState.routePlan.available?.activeAllowanceClients ?? [],
        coach,
        coachee: coachee
          ? {
              ...coachee,
              employeeCode: coacheeCode,
              jobTitle: titles[coachee.jobTitle],
            }
          : null,
        salesman: salesman
          ? {
              ...salesman,
              employeeCode: salesmanCode,
              jobTitle: 'Vendedor',
            }
          : null,
        routes,
        forms,
        weekday,
        weekDate,
        parameters,
        scoreAlgorithmVersion: 'v2.0',
      })
      if (coaching.coachingStage === 'pre-process')
        await services.coaching.checkIn(coaching, { stage: 'pre-process' })
      await dispatch('applyState', coaching)
      commit('UPDATE_ROOT', { lastCoaching: weekDate, creating: false })
    } catch (e) {
      commit('UPDATE_ROOT', { creating: false })
      throw e
    } finally {
      commit('UPDATE_ROOT', { isLoading: false })
    }
  },
  async finish({ dispatch, getters }, coacheeCode) {
    const coaching = getters.current
    const { services } = this.$api
    await services.coaching.checkIn(coaching, { stage: 'post-process' })
    await dispatch('applyState', coaching)
  },
  async feedback({ dispatch, getters }, payload) {
    const coaching = getters.current
    const { services } = this.$api
    await services.coaching.finish(coaching, payload)
    await dispatch('applyState', coaching)
  },
  async expireCoaching({ dispatch, getters }) {
    const coaching = getters.current
    const { services } = this.$api
    await services.coaching.expireCoaching(coaching)
    await dispatch('applyState', coaching)
  },
  async discardCoaching({ dispatch, getters }) {
    const coaching = getters.current
    const { services } = this.$api
    await services.coaching.discardCoaching(coaching)
    await dispatch('applyState', coaching)
  },
  async abortPreProcess({ dispatch, getters }, reason) {
    const coaching = getters.current
    const { services } = this.$api
    await services.coaching.abortPreProcess(coaching, { reason })
    await services.coaching.checkIn(coaching, { stage: coaching.coachingStage })
    await dispatch('applyState', coaching)
  },
  async abortProcess({ dispatch, getters }, reason) {
    const coaching = getters.current
    const { services } = this.$api
    await services.coaching.abortProcess(coaching, { reason })
    await dispatch('applyState', coaching)
  },
  async setForm({ dispatch, getters }, payload) {
    const coaching = getters.current
    const { services } = this.$api
    await services.coaching.setForm(coaching, payload)
    await dispatch('applyState', coaching)
  },
  async answerQuestion({ dispatch, getters }, payload) {
    const coaching = getters.current
    const { services } = this.$api
    await services.coaching.answerQuestion(coaching, payload)
    await dispatch('applyState', coaching)
  },
  async answerDetail({ dispatch, getters }, payload) {
    const coaching = getters.current
    const { services } = this.$api
    await services.coaching.answerDetail(coaching, payload)
    await dispatch('applyState', coaching)
  },
  async checkOut({ dispatch, getters }) {
    const coaching = getters.current
    const { coords } = coaching.teleSales
      ? (await this.$geolocation.position) || {}
      : {}
    const { services } = this.$api
    const payload = {
      analytics: {
        location: !coords
          ? undefined
          : {
              coordinates: {
                coordinates: [coords.longitude, coords.latitude],
              },
            },
      },
    }
    await services.coaching.checkOut(coaching, payload)
    await dispatch('applyState', coaching)
  },
  async checkInPDV({ dispatch, getters }, { approved, ..._payload }) {
    const coaching = getters.current
    let analytics = {}
    if (!coaching.teleSales) {
      const {
        coords: { latitude, longitude },
      } = await this.$geolocation.getCurrentPosition({ maximumAge: Infinity })
      analytics.location = {
        coordinates: {
          coordinates: [longitude, latitude],
        },
      }
    }
    const payload = {
      ..._payload,
      stage: 'process',
      analytics,
    }
    const { services } = this.$api
    await services.coaching.checkIn(coaching, payload)
    await dispatch('applyState', coaching)
  },
  async clientObservation({ dispatch, getters }, payload) {
    const coaching = getters.current
    const { services } = this.$api
    await services.coaching.clientObservation(coaching, payload)
    await dispatch('applyState', coaching)
  },
  async clientPotential({ dispatch, getters }, payload) {
    const coaching = getters.current
    const { services } = this.$api
    await services.coaching.clientPotential(coaching, payload)
    await dispatch('applyState', coaching)
  },
}
